import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, ArrowLeft, User } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || "";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    api(`/blog/${slug}`).then(setPost).catch((e) => {
      if (String(e.message || "").includes("404")) setNotFound(true);
      else setNotFound(true);
    });
  }, [slug]);

  const jsonLd = post ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description || post.subtitle,
    "image": post.hero_image_url ? [post.hero_image_url] : undefined,
    "datePublished": post.published_at,
    "dateModified": post.updated_at,
    "author": { "@type": "Organization", "name": post.author || "WoningAlert" },
    "publisher": { "@type": "Organization", "name": "WoningAlert" },
    "mainEntityOfPage": `${SITE_URL}/blog/${post.slug}`,
  } : null;

  useSeo(post ? {
    title: `${post.title} | WoningAlert Blog`,
    description: post.meta_description || post.subtitle || post.title,
    image: post.hero_image_url,
    jsonLd: jsonLd ? [JSON.stringify(jsonLd)] : [],
  } : { title: "Blog | WoningAlert" });

  if (notFound) return <Navigate to="/blog" replace />;
  if (!post) return <div className="p-10 text-center text-slate-400">Laden…</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-post">
      <article className="mx-auto max-w-3xl px-4 pb-16 pt-8 sm:px-6">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-navy">
          <ArrowLeft size={14} /> Terug naar blog
        </Link>

        <div className="mt-6 flex flex-wrap gap-1 text-[11px]">
          {(post.tags || []).slice(0, 4).map((t) => (
            <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="rounded-full bg-brandblue-light px-2 py-0.5 font-bold text-brandblue hover:opacity-90">#{t}</Link>
          ))}
        </div>

        <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight text-navy sm:text-4xl md:text-5xl" data-testid="post-title">{post.title}</h1>
        {post.subtitle && <p className="mt-4 text-lg text-slate-500">{post.subtitle}</p>}

        <div className="mt-5 flex flex-wrap items-center gap-3 border-y border-slate-100 py-3 text-sm text-slate-500">
          {post.author && <span className="inline-flex items-center gap-1"><User size={13} /> {post.author}</span>}
          <span className="inline-flex items-center gap-1"><Clock size={13} /> {post.read_minutes || 3} min lezen</span>
          <span className="ml-auto text-xs">{new Date(post.published_at).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>

        {post.hero_image_url && (
          <figure className="mt-6">
            <img src={post.hero_image_url} alt={post.hero_image_alt || post.title}
              className="aspect-[16/9] w-full rounded-3xl object-cover shadow-sm" />
            {post.hero_image_attribution && (
              <figcaption className="mt-2 text-center text-xs italic text-slate-400" data-testid="post-attribution">
                {post.hero_image_attribution}
              </figcaption>
            )}
          </figure>
        )}

        <div className="prose prose-slate mt-8 max-w-none prose-lg prose-headings:font-display prose-headings:text-navy prose-headings:tracking-tight prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-2xl prose-h2:font-extrabold prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-lg prose-p:my-5 prose-p:leading-[1.75] prose-p:text-slate-700 prose-li:my-1.5 prose-li:text-slate-700 prose-ul:my-5 prose-ol:my-5 prose-a:text-brandblue prose-a:font-semibold prose-strong:text-navy first:prose-p:mt-0 first:prose-p:text-lg first:prose-p:leading-relaxed first:prose-p:text-slate-600" data-testid="post-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_markdown}</ReactMarkdown>
        </div>

        {/* Newsletter/register CTA */}
        <div className="mt-12 rounded-3xl bg-navy p-6 text-white sm:p-8">
          <h2 className="font-display text-xl font-bold sm:text-2xl">Mis geen enkele nieuwe woning</h2>
          <p className="mt-2 text-sm text-white/80">
            Krijg direct een melding zodra een woning met jouw wensen online komt. Gratis, geen creditcard nodig.
          </p>
          <Link to="/register" className="mt-4 inline-block rounded-full bg-brandblue px-6 py-3 font-bold text-white hover:bg-brandblue/90" data-testid="post-cta">
            Zet gratis alerts aan →
          </Link>
        </div>

        {/* Related posts */}
        {post.related?.length > 0 && (
          <div className="mt-12">
            <h3 className="font-display text-xl font-bold text-navy">Meer lezen</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {post.related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group block overflow-hidden rounded-2xl bg-white shadow-sm transition hover:shadow-md">
                  {r.hero_image_url && <img src={r.hero_image_url} alt="" className="aspect-[16/10] w-full object-cover" />}
                  <div className="p-3">
                    <h4 className="line-clamp-2 text-sm font-bold text-navy group-hover:text-brandblue">{r.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPost;
